import { Drawer } from '@material-ui/core';
import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getTaskDetailsSelector } from '@redux/tasks/tasksSelector';
import { clearPreviewAssessmentDetailsThunk, clearSelectedTaskDetailsThunk, getTaskDetailsThunk } from '@redux/tasks/tasksThunks';
import { TaskStatusEnum, TaskTypesEnum } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { CloseIconButton } from 'components/Button/CloseButton/CloseIconButton';
import { DropDownMenu } from 'components/DropDownMenu/DropDownMenu';
import { FREQUENCY_SELECT_OPTIONS } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { CircleIcon, XSquareIcon } from 'components/Icons';
import { StatusLabel } from 'components/StatusLabel/StatusLabel';
import { Text } from 'components/Typography';
import { ConditionsPreview } from 'containers/Tasks/components/ConditionsPreview/ConditionsPreview';
import { PersonInfoArea } from 'containers/Tasks/components/PersonInfoArea/PersonInfoArea';
import { TaskLogs } from 'containers/Tasks/components/TaskLogs/TaskLogs';
import { TASK_LABEL_STATUSES } from 'containers/Tasks/Tasks.const';
import { Color } from 'utils/color';
import { getFormatedTimeIgnoreTimezone } from 'utils/time';
import { AssessmentResult } from '../AssessmentResult/AssessmentResult';
import style from './TaskInfoDrawer.scss';

type TaskPersonProps = {
  taskId: Nullable<string>;
  onClose: () => void;
  onCancelTask?: (taskId: string, isSingleCancel: boolean) => void;
};

type Props = ConnectedProps<typeof connectRedux> & TaskPersonProps;

const TaskInfoDrawer: React.FC<Props> = ({ onClose, taskId, onCancelTask, taskDetails, fetchTaskDetails, clearTaskDetails, clearAssessmentDetails }) => {
  useEffect(() => {
    if (taskId) {
      if (taskDetails) clearTaskDetails();

      fetchTaskDetails(taskId);
    }
  }, [taskId]);

  useEffect(() => {
    return () => {
      clearTaskDetails();
      clearAssessmentDetails();
    };
  }, []);

  if (!taskId) {
    return null;
  }

  const actualStatus = taskDetails?.status ? TASK_LABEL_STATUSES[taskDetails.status] : null;

  return (
    taskId && (
      <Drawer anchor="right" open={true} onClose={onClose}>
        <div className={style.drawerContainer}>
          <div className={style.drawerButtonHeader}>
            {taskDetails && (
              <DropDownMenu
                menuText="Actions"
                menuItems={[
                  taskDetails.frequency !== FREQUENCY_SELECT_OPTIONS.no_repeat && {
                    text: taskDetails?.status === TaskStatusEnum.SCHEDULED ? 'Cancel this and all future tasks' : 'Cancel all future tasks',
                    icon: () => <CircleIcon width={16} height={16} color={Color.GRAY_950} />,
                    onClick: () => onCancelTask?.(taskId, false)
                  },
                  {
                    text: 'Cancel',
                    icon: () => <XSquareIcon width={16} height={16} />,
                    onClick: () => onCancelTask?.(taskId, true),
                    disabled: !onCancelTask || taskDetails?.status !== TaskStatusEnum.SCHEDULED
                  }
                ]}
              />
            )}
            <CloseIconButton onClick={onClose} />
          </div>
          <div className={style.content}>
            {taskDetails && (
              <>
                <div className={style.titleContainer}>
                  {actualStatus && <StatusLabel label={actualStatus.label} containerClassName={style.topMargin} status={actualStatus.type} />}
                  <Text variant="h5" weight={500} className={style.topMargin}>
                    {taskDetails.taskName}
                  </Text>
                  {taskDetails?.scheduledForDateTime && (
                    <ConditionsPreview
                      frequency={{
                        repeat: taskDetails.frequency,
                        ends: taskDetails.frequencyEnds,
                        date: taskDetails.frequencyEndsOn ? new Date(getFormatedTimeIgnoreTimezone(taskDetails.frequencyEndsOn)) : null,
                        after: taskDetails.endsAfterOccurrences ? Number(taskDetails.endsAfterOccurrences) : undefined
                      }}
                      trigger={taskDetails.triggerCondition}
                      selectedDateTime={new Date(getFormatedTimeIgnoreTimezone(taskDetails.scheduledForDateTime))}
                      expiredDateTime={new Date(getFormatedTimeIgnoreTimezone(taskDetails.expirationDateTime))}
                    />
                  )}
                </div>
                <PersonInfoArea assignee={taskDetails.assignee} />
                {taskDetails.taskType === TaskTypesEnum.ASSESSMENT && taskDetails?.assessmentId ? (
                  <AssessmentResult
                    details={{
                      id: taskDetails.assessmentId,
                      name: taskDetails.assessmentName,
                      description: taskDetails.assessmentDescription,
                      result: taskDetails.assessmentResult
                    }}
                    onClose={onClose}
                  />
                ) : null}
                {taskDetails?.logs?.length && <TaskLogs logsList={taskDetails.logs} />}
              </>
            )}
          </div>
        </div>
      </Drawer>
    )
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    taskDetails: getTaskDetailsSelector(state)
  }),
  {
    fetchTaskDetails: getTaskDetailsThunk,
    clearTaskDetails: clearSelectedTaskDetailsThunk,
    clearAssessmentDetails: clearPreviewAssessmentDetailsThunk
  }
);

export default compose(connectRedux)(TaskInfoDrawer);
